import { memo, useCallback, useMemo, useState } from 'react';

import { Grid, IconButton, useMediaQuery, useTheme } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import clsx from 'clsx';

import { TimePicker } from '../pickers';
import { useStyles } from './AvailabilityPicker.styles';
import { AvailabilityPickerProps } from './AvailabilityPicker.types';

const periodsOrder = ['morning', 'afternoon', 'evening'];

export const AvailabilityPickerMobile = memo(
  ({ availabilityMap, isCenter, classes, onChange }: AvailabilityPickerProps) => {
    const innerClasses = useStyles();
    const [currentPeriodIdx, setCurrentPeriodIdx] = useState(0);

    const currentPeriod = useMemo(() => periodsOrder[currentPeriodIdx], [currentPeriodIdx]);

    const currentSchedule = useMemo(() => availabilityMap?.[currentPeriod] || [], [availabilityMap, currentPeriod]);

    const nextPeriod = useCallback(() => {
      setCurrentPeriodIdx(prev => {
        if (prev === periodsOrder.length - 1) {
          return 0;
        }
        return prev + 1;
      });
    }, []);

    const prevPeriod = useCallback(() => {
      setCurrentPeriodIdx(prev => {
        if (prev === 0) {
          return periodsOrder.length - 1;
        }
        return prev - 1;
      });
    }, []);

    return (
      <Grid
        container
        wrap="nowrap"
        justifyContent={isCenter ? 'center' : 'flex-start'}
        className={clsx(innerClasses.mobileContainer, classes?.root)}
      >
        <div className={innerClasses.absoluteArrowsContainer}>
          <IconButton onClick={prevPeriod} color="primary">
            <ChevronLeft />
          </IconButton>

          <IconButton onClick={nextPeriod} color="primary">
            <ChevronRight />
          </IconButton>
        </div>

        <Grid container item xs={12} justifyContent="center">
          <TimePicker
            alwaysShowScrollbar
            period={currentPeriod}
            schedule={currentSchedule}
            onTimeSelected={onChange}
            className={clsx(innerClasses.mobileTimePicker, classes?.timePicker)}
          />
        </Grid>
      </Grid>
    );
  },
);

export const AvailabilityPickerDesktop = memo(
  ({ availabilityMap, isCenter, classes, onChange }: AvailabilityPickerProps) => {
    const innerClasses = useStyles();

    if (!availabilityMap) {
      return null;
    }

    return (
      <Grid
        container
        wrap="nowrap"
        justifyContent={isCenter ? 'center' : 'flex-start'}
        className={clsx(innerClasses.desktopContainer, classes?.root)}
      >
        {Object.entries(availabilityMap).map(([period, schedule]) => (
          <Grid item md="auto" key={period}>
            <TimePicker
              alwaysShowScrollbar
              period={period}
              schedule={schedule}
              onTimeSelected={onChange}
              className={clsx(innerClasses.timePicker, classes?.timePicker)}
            />
          </Grid>
        ))}
      </Grid>
    );
  },
);

export const AvailabilityPicker = memo((props: AvailabilityPickerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
    noSsr: true,
  });

  const isMobileValue = useMemo(() => props.isMobile ?? isMobile, [props.isMobile, isMobile]);

  return !isMobileValue ? <AvailabilityPickerDesktop {...props} /> : <AvailabilityPickerMobile {...props} />;
});
